import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StepFieldConfig } from "../config/demoSites";
import FormTable from "./FormTable";

interface FormFieldProps {
    field: StepFieldConfig;
    value: any;
    onChange: (value: any) => void;
    allFieldValues?: Record<string, any>;
    onBlur?: () => void;
    errors?: Record<string, string>;
}

export const FormField: React.FC<FormFieldProps> = ({ field, value, onChange, allFieldValues = {}, onBlur, errors = {} }) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    // Check if the field should be displayed based on dependencies
    const shouldShow = () => {
        if (!field.dependsOn) return true;

        const dependencyValue = allFieldValues[field.dependsOn.field];
        const targetValue = field.dependsOn.value;
        const condition = field.dependsOn.condition || "equals";

        switch (condition) {
            case "equals":
                return dependencyValue === targetValue;
            case "notEquals":
                return dependencyValue !== targetValue;
            case "contains":
                return Array.isArray(dependencyValue) && dependencyValue.includes(targetValue);
            case "notContains":
                return !Array.isArray(dependencyValue) || !dependencyValue.includes(targetValue);
            case "greater":
                return Number(dependencyValue) > Number(targetValue);
            case "less":
                return Number(dependencyValue) < Number(targetValue);
            default:
                return true;
        }
    };

    // Apply default value if none is provided and field has default
    useEffect(() => {
        if (value === undefined && field.defaultValue !== undefined) {
            onChange(field.defaultValue);
        }
    }, [field.defaultValue]);

    // Don't render if dependency conditions aren't met
    if (!shouldShow()) return null;

    // Translations
    const label = t(field.labelKey, field.label);
    const placeholder = field.placeholderKey ? t(field.placeholderKey, `Enter ${field.label}`) : "";
    const description = field.descriptionKey ? t(field.descriptionKey, "") : "";
    const helpText = field.helpTextKey ? t(field.helpTextKey, "") : "";
    const tooltip = field.tooltipKey ? t(field.tooltipKey, "") : "";
    const hint = field.hintKey ? t(field.hintKey, "") : "";
    const error = errors[field.fieldName] || "";

    // Validation error message
    const getPatternErrorMessage = () => {
        if (field.validationRules?.patternErrorKey) {
            return t(field.validationRules.patternErrorKey, `Invalid ${field.label.toLowerCase()}`);
        }
        return "";
    };

    // Common props for input elements
    const commonProps = {
        id: field.fieldName,
        name: field.fieldName,
        required: field.required,
        readOnly: field.readOnly,
        disabled: field.disabled,
        autoComplete: field.autoComplete,
        className: `w-full rounded border ${error ? "border-red-500" : "border-gray-300"} p-2 ${field.className || ""}`,
        onBlur
    };

    const renderField = () => {
        switch (field.fieldType) {
            case "text":
            case "email":
            case "tel":
            case "url":
            case "search":
            case "number":
                return (
                    <div className="relative">
                        {field.iconStart && (
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                {/* Icon component would go here */}
                                <i className={field.iconStart}></i>
                            </span>
                        )}

                        <input
                            type={field.fieldType}
                            value={value || ""}
                            onChange={e => onChange(e.target.value)}
                            placeholder={placeholder}
                            min={field.validationRules?.min}
                            max={field.validationRules?.max}
                            step={field.validationRules?.step}
                            maxLength={field.validationRules?.maxLength}
                            minLength={field.validationRules?.minLength}
                            pattern={field.validationRules?.pattern}
                            title={getPatternErrorMessage()}
                            {...commonProps}
                            className={`${commonProps.className} ${field.iconStart ? "pl-10" : ""} ${field.iconEnd ? "pr-10" : ""}`}
                        />

                        {field.iconEnd && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                {/* Icon component would go here */}
                                <i className={field.iconEnd}></i>
                            </span>
                        )}
                    </div>
                );

            case "password":
                return (
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            value={value || ""}
                            onChange={e => onChange(e.target.value)}
                            placeholder={placeholder}
                            {...commonProps}
                            className={`${commonProps.className} pr-10`}
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {/* This would be an eye icon */}
                            {showPassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                    </div>
                );

            case "textarea":
                return (
                    <textarea
                        value={value || ""}
                        onChange={e => onChange(e.target.value)}
                        placeholder={placeholder}
                        rows={field.rows || 3}
                        cols={field.cols}
                        maxLength={field.validationRules?.maxLength}
                        minLength={field.validationRules?.minLength}
                        {...commonProps}
                    ></textarea>
                );

            case "select":
                return (
                    <select value={value || ""} onChange={e => onChange(e.target.value)} {...commonProps}>
                        <option value="">{placeholder || `Select ${label}`}</option>
                        {field.optionsKeys &&
                            Object.entries(field.optionsKeys).map(([key, translationKey]) => (
                                <option key={key} value={key}>
                                    {t(translationKey, key)}
                                </option>
                            ))}
                    </select>
                );

            case "multiselect":
                return (
                    <select
                        value={Array.isArray(value) ? value : []}
                        onChange={e => {
                            const options = Array.from(e.target.selectedOptions).map(option => option.value);
                            onChange(options);
                        }}
                        multiple={true}
                        {...commonProps}
                        className={`${commonProps.className} min-h-[100px]`}
                    >
                        {field.optionsKeys &&
                            Object.entries(field.optionsKeys).map(([key, translationKey]) => (
                                <option key={key} value={key}>
                                    {t(translationKey, key)}
                                </option>
                            ))}
                    </select>
                );

            case "radio":
                return (
                    <div className="space-y-2">
                        {field.optionsKeys &&
                            Object.entries(field.optionsKeys).map(([key, translationKey]) => (
                                <label key={key} className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name={field.fieldName}
                                        value={key}
                                        checked={value === key}
                                        onChange={() => onChange(key)}
                                        required={field.required}
                                        disabled={field.disabled}
                                        className="h-4 w-4"
                                    />
                                    <span>{t(translationKey, key)}</span>
                                </label>
                            ))}
                    </div>
                );

            case "checkbox":
                return (
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={value === true || value === "true"}
                            onChange={e => onChange(e.target.checked ? true : false)}
                            required={field.required}
                            disabled={field.disabled}
                            className="h-4 w-4"
                        />
                        <span>{label}</span>
                    </label>
                );

            case "toggle":
                return (
                    <label className="relative inline-flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={value === true || value === "true"}
                            onChange={e => onChange(e.target.checked)}
                            className="peer sr-only"
                            required={field.required}
                            disabled={field.disabled}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300"></div>
                        <span className="ml-3">{label}</span>
                    </label>
                );

            case "file":
                return (
                    <div>
                        <input
                            type="file"
                            onChange={e => {
                                const files = e.target.files;
                                if (files) {
                                    onChange(field.multiple ? Array.from(files) : files[0]);
                                }
                            }}
                            accept={field.accept}
                            multiple={field.multiple}
                            {...commonProps}
                            className={`${commonProps.className} cursor-pointer file:mr-4 file:cursor-pointer file:rounded file:border-0 file:bg-primary file:px-4 file:py-2 file:text-white`}
                        />
                        {value && !field.multiple && <p className="mt-1 text-sm text-gray-500">{typeof value === "object" ? value.name : value}</p>}
                        {value && field.multiple && Array.isArray(value) && (
                            <ul className="mt-2 space-y-1">
                                {value.map((file: File, index: number) => (
                                    <li key={index} className="text-sm text-gray-700">
                                        {file.name} ({Math.round(file.size / 1024)} KB)
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                );

            case "date":
            case "time":
            case "datetime-local":
                return (
                    <input
                        type={field.fieldType}
                        value={value || ""}
                        onChange={e => onChange(e.target.value)}
                        min={field.validationRules?.min}
                        max={field.validationRules?.max}
                        step={field.validationRules?.step}
                        {...commonProps}
                    />
                );

            case "range":
                return (
                    <div>
                        <input
                            type="range"
                            value={value || field.validationRules?.min || 0}
                            onChange={e => onChange(e.target.value)}
                            min={field.validationRules?.min}
                            max={field.validationRules?.max}
                            step={field.validationRules?.step}
                            {...commonProps}
                            className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-600">
                            <span>{field.validationRules?.min || 0}</span>
                            <span>{value || field.validationRules?.min || 0}</span>
                            <span>{field.validationRules?.max || 100}</span>
                        </div>
                    </div>
                );

            case "color":
                return (
                    <div className="flex items-center space-x-2">
                        <input
                            type="color"
                            value={value || "#000000"}
                            onChange={e => onChange(e.target.value)}
                            {...commonProps}
                            className="h-10 w-10 rounded p-1"
                        />
                        <span className="text-sm">{value || "#000000"}</span>
                    </div>
                );
            case "table":
                return <FormTable field={field} value={value || []} onChange={onChange} />;
            default:
                return <p className="text-red-500">Unsupported field type: {field.fieldType}</p>;
        }
    };

    // Tooltip component
    const renderTooltip = () => {
        if (!tooltip) return null;

        return (
            <div className="group relative ml-1 inline-block">
                <span className="cursor-help text-sm text-gray-500">ℹ️</span>
                <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-48 -translate-x-1/2 rounded bg-gray-800 p-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                    {tooltip}
                </div>
            </div>
        );
    };

    // Fix: Remove the check for 'hidden' field type that's causing the TypeScript error
    return (
        <div className="mb-4">
            {/* Label - not shown for checkbox and toggle as they include their own label */}
            {field.fieldType !== "checkbox" && field.fieldType !== "toggle" && (
                <label htmlFor={field.fieldName} className="mb-1 flex items-center font-medium">
                    {label}
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                    {renderTooltip()}
                </label>
            )}

            {/* Field */}
            {renderField()}

            {/* Description */}
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}

            {/* Help text */}
            {helpText && <p className="mt-1 text-sm text-blue-600">{helpText}</p>}

            {/* Hint */}
            {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}

            {/* Error message */}
            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
        </div>
    );
};

export default FormField;
